// © 2026 AttainIP Engineering
import { useState, useMemo, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchUsers } from '@/api/queries';
import { Card } from '@/components/Card';
import { UserRowIdentity } from '@/components/UserRowIdentity';
import { UserRowOrganisation } from '@/components/UserRowOrganisation';
import { useAdminPrefs } from '@/lib/adminPrefs';
import { rowRoles, type UserDirectoryRow } from '@/lib/userDirectoryRow';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1); // 1-indexed
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const { hideInternal } = useAdminPrefs();

  // Reset to page 1 whenever the filters change so the user doesn't
  // land on an empty page.
  useEffect(() => {
    setPage(1);
  }, [search, pageSize, hideInternal]);

  const offset = (page - 1) * pageSize;

  // No debounce — fires on every keystroke. `placeholderData:
  // keepPreviousData` keeps the previous results visible until the
  // next one lands, so the table never flashes empty between fetches.
  // The server-side LIKE on email + display_name is cheap at any
  // realistic user volume.
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['users', { offset, pageSize, search, hideInternal }],
    queryFn: () =>
      fetchUsers({
        limit: pageSize,
        offset,
        search: search || undefined,
        hide_internal: hideInternal,
        sort_by: 'last_login_at',
        order: 'desc',
      }),
    placeholderData: keepPreviousData,
  });

  const users: any[] = data?.data || [];
  const total: number = data?.pagination?.total ?? users.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = users.length === 0 ? 0 : offset;

  // If the upstream returned an error envelope it surfaces here.
  const upstreamError = useMemo(() => {
    if (!data || !('error' in data) || data.error !== true) return null;
    return data.detail || `Upstream error (status ${data.status ?? '?'})`;
  }, [data]);

  if (isLoading) return <p className="text-gray-500">Loading users...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">Users ({total})</h1>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {isFetching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
            …
          </span>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">
          Failed to load users: {(error as Error).message}
        </p>
      )}
      {upstreamError && (
        <p className="text-sm text-red-600 mb-3">{upstreamError}</p>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 font-medium">Email</th>
              {/* One row per (person, organisation) — RFC-0510 A7. Without
                  this column a human with three identities is three
                  identical-looking rows. */}
              <th className="pb-2 font-medium">Organisation</th>
              {/* Two axes, never one list (Core's role landscape, 2026-09-02):
                  Type is what KIND of identity this is and carries no
                  permissions; Role is what the person may do. */}
              <th className="pb-2 font-medium">Type</th>
              <th className="pb-2 font-medium">Role</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium">Last login ↓</th>
              <th className="pb-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: UserDirectoryRow & Record<string, any>) => (
              <tr
                key={user.id}
                data-testid={`user-row-${user.id}`}
                className="border-b last:border-0 hover:bg-gray-50"
              >
                <td className="py-2">
                  <Link to={`/users/${user.id}`} className="text-blue-600 hover:underline">
                    {user.display_name || user.name || '—'}
                  </Link>
                </td>
                <td className="py-2 font-mono text-xs">{user.email}</td>
                <td className="py-2">
                  <UserRowOrganisation row={user} />
                </td>
                <td className="py-2">
                  <UserRowIdentity row={user} />
                </td>
                <td className="py-2 text-xs text-gray-500">
                  {rowRoles(user).join(', ') || '—'}
                </td>
                <td className="py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {user.status || 'active'}
                  </span>
                </td>
                <td className="py-2 text-xs text-gray-400">
                  {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
                </td>
                <td className="py-2 text-xs text-gray-400">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && !upstreamError && (
          <p className="text-gray-400 text-sm py-4 text-center">
            {search ? `No users matching "${search}"` : 'No users found'}
          </p>
        )}
        {total > 0 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-gray-500">
            <div>
              Showing <span className="font-medium">{startIdx + 1}</span>–
              <span className="font-medium">{startIdx + users.length}</span>{' '}
              of <span className="font-medium">{total}</span>
              {search && ` matching "${search}"`}
              {hideInternal && ` (internal hidden)`}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2">
                <span>Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1 || isFetching}
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="px-2">
                  Page <span className="font-medium">{currentPage}</span> / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages || isFetching}
                  className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
